import { Component } from '@theme/component';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * Updates the product page description when a variant-specific description is available.
 *
 * @extends {Component}
 */
class ProductDescriptionComponent extends Component {
  /** @type {Record<string, string> | undefined} */
  #variantDescriptions;

  connectedCallback() {
    super.connectedCallback();
    const target = this.closest('[id*="ProductInformation-"]');
    target?.addEventListener(ThemeEvents.variantSelected, this.updateDescriptionImmediately);
    target?.addEventListener(ThemeEvents.variantUpdate, this.updateDescription);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const target = this.closest('[id*="ProductInformation-"]');
    target?.removeEventListener(ThemeEvents.variantSelected, this.updateDescriptionImmediately);
    target?.removeEventListener(ThemeEvents.variantUpdate, this.updateDescription);
  }

  /**
   * Updates the description before the Section Rendering API response returns.
   * @param {VariantSelectedEvent} event
   */
  updateDescriptionImmediately = (event) => {
    if (!(event.target instanceof HTMLElement) || event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const selectedOption = event.target.querySelector(`[data-option-value-id="${event.detail.resource.id}"]`);
    if (!(selectedOption instanceof HTMLElement)) return;

    const variantId = selectedOption.dataset.variantId;
    if (!variantId) return;

    const description = this.#getVariantDescriptions()[variantId];
    if (description === undefined) return;

    this.#setDescriptionHtml(description);
  };

  /**
   * @param {VariantUpdateEvent} event
   */
  updateDescription = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    }

    if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newDescription = event.detail.data.html.querySelector(
      `product-description-component[data-block-id="${this.dataset.blockId}"]`
    );

    if (newDescription) {
      morph(this, newDescription);
      this.#variantDescriptions = undefined;
    }
  };

  /**
   * @returns {Record<string, string>}
   */
  #getVariantDescriptions() {
    if (this.#variantDescriptions) return this.#variantDescriptions;

    const script = this.querySelector('script[data-variant-descriptions]');
    if (!script?.textContent) {
      this.#variantDescriptions = {};
      return this.#variantDescriptions;
    }

    try {
      this.#variantDescriptions = JSON.parse(script.textContent);
    } catch {
      this.#variantDescriptions = {};
    }

    return this.#variantDescriptions;
  }

  /**
   * @param {string} html
   */
  #setDescriptionHtml(html) {
    const descriptionElement = this.querySelector(':scope > .text-block');
    if (!descriptionElement) return;

    descriptionElement.innerHTML = html;
  }
}

if (!customElements.get('product-description-component')) {
  customElements.define('product-description-component', ProductDescriptionComponent);
}
